import Breadcrumb from "components/Common/Breadcrumb"
import TableContainer from "components/Common/DataTableContainer"
import DashboardLoader from "components/Loader/DashboardLoader"
import DeleteModal from "pages/Calendar/DeleteModal"
import React, { useMemo, useState } from "react"
import { Link } from "react-router-dom";
import { Row, Col, Card, CardBody, Button } from "reactstrap"
import {
  useGetAllTransactionsQuery,
  useDeleteTransactionMutation,
} from "store/features/transaction/transactionManagementApi"
import AddTransactionModal from "components/Modals/AddTransactionModal"
import EditTransactionModal from "components/Modals/EditTransactionModal"

const Transactions = () => {
  const {
    data: transactions = [],
    isLoading,
    refetch,
  } = useGetAllTransactionsQuery()
  const [deleteTransaction, { isLoading: deletingTransaction }] =
    useDeleteTransactionMutation()

  const [deleteModal, setDeleteModal] = useState(false)
  const [selectedTransactionId, setSelectedTransactionId] = useState(null)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [selectedTransaction, setSelectedTransaction] = useState(null)
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")

  // Filter transactions by date range
  const filteredTransactions = useMemo(() => {
    if (!transactions.length) return []

    let filtered = transactions

    // Filter by date range if dates are selected
    if (startDate && endDate) {
      filtered = transactions.filter(transaction => {
        if (!transaction.date) return false
        const transactionDate = new Date(transaction.date)
        const start = new Date(startDate)
        const end = new Date(endDate)
        return transactionDate >= start && transactionDate <= end
      })
    }

    return filtered
  }, [transactions, startDate, endDate])

  // Group transactions by month
  const transactionsByMonth = useMemo(() => {
    const grouped = {}

    filteredTransactions.forEach(transaction => {
      if (!transaction.date) return

      const date = new Date(transaction.date)
      const monthYear = date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
      })
      const monthKey = date.toISOString().slice(0, 7) // YYYY-MM for sorting

      if (!grouped[monthKey]) {
        grouped[monthKey] = {
          monthName: monthYear,
          transactions: [],
          totalEarning: 0,
          totalDeposit: 0,
          totalCosting: 0,
          netAmount: 0,
        }
      }

      grouped[monthKey].transactions.push(transaction)

      const amount = parseFloat(transaction.amount || 0)
      if (transaction.transaction_type === "earning") {
        grouped[monthKey].totalEarning += amount
      } else if (transaction.transaction_type === "deposit") {
        grouped[monthKey].totalDeposit += amount
      } else if (transaction.transaction_type === "cost") {
        grouped[monthKey].totalCosting += amount
      }

      grouped[monthKey].netAmount =
        grouped[monthKey].totalEarning +
        grouped[monthKey].totalDeposit -
        grouped[monthKey].totalCosting
    })

    // Sort months in descending order (newest first)
    return Object.entries(grouped)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([key, value]) => value)
  }, [filteredTransactions])

  // Calculate overall totals
  const overallTotals = useMemo(() => {
    const totals = {
      totalEarning: 0,
      totalDeposit: 0,
      totalCosting: 0,
      netAmount: 0,
    }

    transactionsByMonth.forEach(month => {
      totals.totalEarning += month.totalEarning
      totals.totalDeposit += month.totalDeposit
      totals.totalCosting += month.totalCosting
    })

    totals.netAmount =
      totals.totalEarning + totals.totalDeposit - totals.totalCosting
    return totals
  }, [transactionsByMonth])

  const showDeleteModal = id => {
    setSelectedTransactionId(id)
    setDeleteModal(true)
  }

  const handleDeleteTransaction = () => {
    deleteTransaction(selectedTransactionId)
    setDeleteModal(false)
  }

  const handleEditTransaction = transaction => {
    setSelectedTransaction(transaction)
    setEditModalOpen(true)
  }

  const handleTransactionUpdate = () => {
    refetch()
    setEditModalOpen(false)
  }

  const handleAddTransaction = () => {
    setAddModalOpen(true)
  }

  const handleTransactionCreate = () => {
    refetch()
    setAddModalOpen(false)
  }

  const formatCurrency = amount => {
    return new Intl.NumberFormat("en-BD", {
      style: "currency",
      currency: "BDT",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount) || 0)
  }

  const formatDate = dateString => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleDateString("en-US")
  }

  const getTypeBadge = type => {
    const typeConfig = {
      earning: { color: "success", text: "Earning" },
      deposit: { color: "info", text: "Deposit" },
      cost: { color: "danger", text: "Cost" },
    }
    const config = typeConfig[type] || { color: "secondary", text: type }
    return `<span class="badge bg-${config.color}">${config.text}</span>`
  }

  const columns = useMemo(
    () => [
      {
        header: "Date",
        accessorKey: "date",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.date),
      },
      {
        header: "Type",
        accessorKey: "transaction_type",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div
            dangerouslySetInnerHTML={{
              __html: getTypeBadge(row.original.transaction_type),
            }}
          />
        ),
      },
      {
        header: "Amount",
        accessorKey: "amount",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          const type = row.original.transaction_type
          let textClass = "text-secondary"
          if (type === "earning" || type === "deposit") {
            textClass = "text-success"
          } else if (type === "cost") {
            textClass = "text-danger"
          }

          return (
            <div className={`fw-semibold ${textClass}`}>
              {formatCurrency(parseFloat(row.original.amount || 0))}
            </div>
          )
        },
      },
      {
        header: "Description",
        accessorKey: "description",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => row.original.description || "No description",
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="d-flex align-items-center gap-1">
            <button
              onClick={() => handleEditTransaction(row.original)}
              className="btn btn-primary btn-sm"
            >
              <i className="fas fa-edit me-1"></i> Edit
            </button>
            <button
              className="btn btn-danger btn-sm"
              onClick={() => showDeleteModal(row.original.id)}
            >
              <i className="fas fa-trash me-1"></i>
              {deletingTransaction ? "Deleting..." : "Delete"}
            </button>
          </div>
        ),
      },
    ],
    [deletingTransaction]
  )

  if (isLoading) {
    return <DashboardLoader />
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
       <div className="mb-4">
  <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">

    {/* Left Section */}
    <div>
      <Breadcrumb
        title="Transactions"
        breadcrumbItem="Transactions"
      />
     
    </div>

    {/* Right Section */}
    <div className="d-flex flex-wrap gap-2">

      <Button
        color="primary"
        onClick={handleAddTransaction}
        className="d-flex align-items-center"
      >
        <i className="fas fa-plus-circle me-2"></i>
        Transaction
      </Button>

      <Link
        to="/all-fixed-costs"
        className="btn btn-outline-primary d-flex align-items-center"
      >
        <i className="fas fa-wallet me-2"></i>
        Fixed Costs
      </Link>

    </div>

  </div>
</div>

        


        {/* Summary Cards */}
        <Row className="mb-4">
          <Col lg={3}>
            <Card className="card-animate">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <p className="text-uppercase fw-medium text-muted mb-0">
                      Total Earning
                    </p>
                    <h4 className="fs-22 fw-semibold ff-secondary mb-0 text-success">
                      {formatCurrency(overallTotals.totalEarning)}
                    </h4>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-sm rounded-circle bg-success bg-opacity-10">
                      <i className="fas fa-arrow-up text-success fs-18"></i>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={3}>
            <Card className="card-animate">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <p className="text-uppercase fw-medium text-muted mb-0">
                      Total Deposit
                    </p>
                    <h4 className="fs-22 fw-semibold ff-secondary mb-0 text-info">
                      {formatCurrency(overallTotals.totalDeposit)}
                    </h4>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-sm rounded-circle bg-info bg-opacity-10">
                      <i className="fas fa-money-bill-wave text-info fs-18"></i>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={3}>
            <Card className="card-animate">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <p className="text-uppercase fw-medium text-muted mb-0">
                      Total Cost
                    </p>
                    <h4 className="fs-22 fw-semibold ff-secondary mb-0 text-danger">
                      {formatCurrency(overallTotals.totalCosting)}
                    </h4>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-sm rounded-circle bg-danger bg-opacity-10">
                      <i className="fas fa-arrow-down text-danger fs-18"></i>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={3}>
            <Card className="card-animate">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <p className="text-uppercase fw-medium text-muted mb-0">
                      Net Amount
                    </p>
                    <h4
                      className={`fs-22 fw-semibold ff-secondary mb-0 ${
                        overallTotals.netAmount >= 0
                          ? "text-success"
                          : "text-danger"
                      }`}
                    >
                      {formatCurrency(overallTotals.netAmount)}
                    </h4>
                  </div>
                  <div className="flex-shrink-0">
                    <div
                      className={`avatar-sm rounded-circle ${
                        overallTotals.netAmount >= 0
                          ? "bg-success bg-opacity-10"
                          : "bg-danger bg-opacity-10"
                      }`}
                    >
                      <i
                        className={`fas ${
                          overallTotals.netAmount >= 0
                            ? "fa-chart-line"
                            : "fa-chart-bar"
                        } ${
                          overallTotals.netAmount >= 0
                            ? "text-success"
                            : "text-danger"
                        } fs-18`}
                      ></i>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Filters and Actions */}
        <Card className="mb-4">
          <CardBody>
            <Row className="align-items-center">
              <Col lg={3}>
                <div className="mb-3">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                  />
                </div>
              </Col>
              <Col lg={3}>
                <div className="mb-3">
                  <label className="form-label">End Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                  />
                </div>
              </Col>
              <Col lg={6}>
                <div className="d-flex align-items-center justify-content-between gap-2 mt-3">
                  <Button
                    color="light"
                    onClick={() => {
                      setStartDate("")
                      setEndDate("")
                    }}
                  >
                    <i className="fas fa-times"></i>
                  </Button>
                 
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Month-wise Transactions */}
        {transactionsByMonth.map((monthData, index) => (
          <Card key={index} className="mb-4">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="card-title mb-0">{monthData.monthName}</h5>
                <div className="d-flex gap-3">
                  <span className="text-success">
                    <i className="fas fa-arrow-up me-1"></i>
                    Earning: {formatCurrency(monthData.totalEarning)}
                  </span>
                  <span className="text-info">
                    <i className="fas fa-money-bill-wave me-1"></i>
                    Deposit: {formatCurrency(monthData.totalDeposit)}
                  </span>
                  <span className="text-danger">
                    <i className="fas fa-arrow-down me-1"></i>
                    Cost: {formatCurrency(monthData.totalCosting)}
                  </span>
                  <span
                    className={
                      monthData.netAmount >= 0 ? "text-success" : "text-danger"
                    }
                  >
                    <i
                      className={`fas ${
                        monthData.netAmount >= 0
                          ? "fa-chart-line"
                          : "fa-chart-bar"
                      } me-1`}
                    ></i>
                    Net: {formatCurrency(monthData.netAmount)}
                  </span>
                </div>
              </div>

              <TableContainer
                columns={columns}
                data={monthData.transactions.reverse()}
                isGlobalFilter={false}
                isPagination={monthData.transactions.length > 10}
                SearchPlaceholder="Search transactions..."
                pagination="pagination"
                paginationWrapper="dataTables_paginate paging_simple_numbers"
                tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
              />
            </CardBody>
          </Card>
        ))}

        {/* No Data Message */}
        {transactionsByMonth.length === 0 && (
          <Card>
            <CardBody>
              <div className="text-center py-5">
                <i className="fas fa-exchange-alt display-1 text-muted mb-3"></i>
                <h4>No Transactions Found</h4>
                <p className="text-muted mb-4">
                  {startDate || endDate
                    ? "No transactions match your date filter criteria."
                    : "No transactions recorded yet."}
                </p>
                <Button color="primary" onClick={handleAddTransaction}>
                  <i className="fas fa-plus me-2"></i>Add Your First Transaction
                </Button>
              </div>
            </CardBody>
          </Card>
        )}

        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDeleteTransaction}
          loader={deletingTransaction}
          onCloseClick={() => setDeleteModal(false)}
        />

        {selectedTransaction && (
          <EditTransactionModal
            isOpen={editModalOpen}
            toggle={() => setEditModalOpen(false)}
            transaction={selectedTransaction}
            onTransactionUpdate={handleTransactionUpdate}
          />
        )}

        <AddTransactionModal
          isOpen={addModalOpen}
          toggle={() => setAddModalOpen(false)}
          onTransactionCreate={handleTransactionCreate}
        />
      </div>
    </div>
  )
}

export default Transactions

// import Breadcrumb from "components/Common/Breadcrumb"
// import TableContainer from "components/Common/DataTableContainer"
// import DashboardLoader from "components/Loader/DashboardLoader"
// import DeleteModal from "pages/Calendar/DeleteModal"
// import React, { useMemo, useState } from "react"
// import { Row, Col, Card, CardBody, Button } from "reactstrap"
// import {
//   useGetAllTransactionsQuery,
//   useDeleteTransactionMutation,
// } from "store/features/transaction/transactionManagementApi"
// import AddTransactionModal from "components/Modals/AddTransactionModal"
// import EditTransactionModal from "components/Modals/EditTransactionModal"

// const Transactions = () => {
//   const {
//     data: transactions = [],
//     isLoading,
//     refetch,
//   } = useGetAllTransactionsQuery()
//   const [deleteTransaction, { isLoading: deletingTransaction }] =
//     useDeleteTransactionMutation()

//   const [deleteModal, setDeleteModal] = useState(false)
//   const [selectedTransactionId, setSelectedTransactionId] = useState(null)
//   const [editModalOpen, setEditModalOpen] = useState(false)
//   const [addModalOpen, setAddModalOpen] = useState(false)
//   const [selectedTransaction, setSelectedTransaction] = useState(null)
//   const [startDate, setStartDate] = useState("")
//   const [endDate, setEndDate] = useState("")

//   // Filter transactions by date range
//   const filteredTransactions = useMemo(() => {
//     if (!transactions.length) return []

//     let filtered = transactions

//     // Filter by date range if dates are selected
//     if (startDate && endDate) {
//       filtered = transactions.filter(transaction => {
//         if (!transaction.date) return false
//         const transactionDate = new Date(transaction.date)
//         const start = new Date(startDate)
//         const end = new Date(endDate)
//         return transactionDate >= start && transactionDate <= end
//       })
//     }

//     return filtered
//   }, [transactions, startDate, endDate])

//   // Group transactions by month
//   const transactionsByMonth = useMemo(() => {
//     const grouped = {}

//     filteredTransactions.forEach(transaction => {
//       if (!transaction.date) return

//       const date = new Date(transaction.date)
//       const monthYear = date.toLocaleDateString("en-US", {
//         year: "numeric",
//         month: "long",
//       })
//       const monthKey = date.toISOString().slice(0, 7) // YYYY-MM for sorting

//       if (!grouped[monthKey]) {
//         grouped[monthKey] = {
//           monthName: monthYear,
//           transactions: [],
//           totalEarning: 0,
//           totalCosting: 0,
//           netAmount: 0,
//         }
//       }

//       grouped[monthKey].transactions.push(transaction)

//       const amount = parseFloat(transaction.amount || 0)
//       if (transaction.transaction_type === "earning") {
//         grouped[monthKey].totalEarning += amount
//       } else if (transaction.transaction_type === "cost") {
//         grouped[monthKey].totalCosting += amount
//       }

//       grouped[monthKey].netAmount =
//         grouped[monthKey].totalEarning - grouped[monthKey].totalCosting
//     })

//     // Sort months in descending order (newest first)
//     return Object.entries(grouped)
//       .sort(([a], [b]) => b.localeCompare(a))
//       .map(([key, value]) => value)
//   }, [filteredTransactions])

//   // Calculate overall totals
//   const overallTotals = useMemo(() => {
//     const totals = {
//       totalEarning: 0,
//       totalCosting: 0,
//       netAmount: 0,
//     }

//     transactionsByMonth.forEach(month => {
//       totals.totalEarning += month.totalEarning
//       totals.totalCosting += month.totalCosting
//     })

//     totals.netAmount = totals.totalEarning - totals.totalCosting
//     return totals
//   }, [transactionsByMonth])

//   const showDeleteModal = id => {
//     setSelectedTransactionId(id)
//     setDeleteModal(true)
//   }

//   const handleDeleteTransaction = () => {
//     deleteTransaction(selectedTransactionId)
//     setDeleteModal(false)
//   }

//   const handleEditTransaction = transaction => {
//     setSelectedTransaction(transaction)
//     setEditModalOpen(true)
//   }

//   const handleTransactionUpdate = () => {
//     refetch()
//     setEditModalOpen(false)
//   }

//   const handleAddTransaction = () => {
//     setAddModalOpen(true)
//   }

//   const handleTransactionCreate = () => {
//     refetch()
//     setAddModalOpen(false)
//   }

//   const formatCurrency = amount => {
//   return new Intl.NumberFormat("en-BD", {
//     style: "currency",
//     currency: "BDT",
//     minimumFractionDigits: 2,
//     maximumFractionDigits: 2,
//   }).format(Number(amount) || 0);
// };

//   const formatDate = dateString => {
//     if (!dateString) return "N/A"
//     return new Date(dateString).toLocaleDateString("en-US")
//   }

//   const getTypeBadge = type => {
//     const typeConfig = {
//       earning: { color: "success", text: "Earning" },
//       cost: { color: "danger", text: "Cost" },
//     }
//     const config = typeConfig[type] || { color: "secondary", text: type }
//     return `<span class="badge bg-${config.color}">${config.text}</span>`
//   }

//   const columns = useMemo(
//     () => [
//       {
//         header: "Date",
//         accessorKey: "date",
//         enableColumnFilter: false,
//         enableSorting: true,
//         cell: ({ row }) => formatDate(row.original.date),
//       },
//       {
//         header: "Type",
//         accessorKey: "transaction_type",
//         enableColumnFilter: false,
//         enableSorting: true,
//         cell: ({ row }) => (
//           <div
//             dangerouslySetInnerHTML={{
//               __html: getTypeBadge(row.original.transaction_type),
//             }}
//           />
//         ),
//       },
//       {
//         header: "Amount",
//         accessorKey: "amount",
//         enableColumnFilter: false,
//         enableSorting: true,
//         cell: ({ row }) => (
//           <div
//             className={`fw-semibold ${
//               row.original.transaction_type === "earning"
//                 ? "text-success"
//                 : "text-danger"
//             }`}
//           >
//             {formatCurrency(parseFloat(row.original.amount || 0))}
//           </div>
//         ),
//       },
//       {
//         header: "Description",
//         accessorKey: "description",
//         enableColumnFilter: false,
//         enableSorting: true,
//         cell: ({ row }) => row.original.description || "No description",
//       },
//       {
//         header: "Action",
//         accessorKey: "action",
//         enableColumnFilter: false,
//         enableSorting: true,
//         cell: ({ row }) => (
//           <div className="d-flex align-items-center gap-1">
//             <button
//               onClick={() => handleEditTransaction(row.original)}
//               className="btn btn-primary btn-sm"
//             >
//               <i className="fas fa-edit me-1"></i> Edit
//             </button>
//             <button
//               className="btn btn-danger btn-sm"
//               onClick={() => showDeleteModal(row.original.id)}
//             >
//               <i className="fas fa-trash me-1"></i>
//               {deletingTransaction ? "Deleting..." : "Delete"}
//             </button>
//           </div>
//         ),
//       },
//     ],
//     [deletingTransaction]
//   )

//   if (isLoading) {
//     return <DashboardLoader />
//   }

//   return (
//     <div className="page-content">
//       <div className="container-fluid">
//         <Breadcrumb title="Transactions" breadcrumbItem="All Transactions" />

//         {/* Summary Cards */}
//         <Row className="mb-4">
//           <Col lg={3}>
//             <Card className="card-animate">
//               <CardBody>
//                 <div className="d-flex align-items-center">
//                   <div className="flex-grow-1">
//                     <p className="text-uppercase fw-medium text-muted mb-0">
//                       Total Earning
//                     </p>
//                     <h4 className="fs-22 fw-semibold ff-secondary mb-0 text-success">
//                       {formatCurrency(overallTotals.totalEarning)}
//                     </h4>
//                   </div>
//                   <div className="flex-shrink-0">
//                     <div className="avatar-sm rounded-circle bg-success bg-opacity-10">
//                       <i className="fas fa-arrow-up text-success fs-18"></i>
//                     </div>
//                   </div>
//                 </div>
//               </CardBody>
//             </Card>
//           </Col>

//           <Col lg={3}>
//             <Card className="card-animate">
//               <CardBody>
//                 <div className="d-flex align-items-center">
//                   <div className="flex-grow-1">
//                     <p className="text-uppercase fw-medium text-muted mb-0">
//                       Total Cost
//                     </p>
//                     <h4 className="fs-22 fw-semibold ff-secondary mb-0 text-danger">
//                       {formatCurrency(overallTotals.totalCosting)}
//                     </h4>
//                   </div>
//                   <div className="flex-shrink-0">
//                     <div className="avatar-sm rounded-circle bg-danger bg-opacity-10">
//                       <i className="fas fa-arrow-down text-danger fs-18"></i>
//                     </div>
//                   </div>
//                 </div>
//               </CardBody>
//             </Card>
//           </Col>

//           <Col lg={3}>
//             <Card className="card-animate">
//               <CardBody>
//                 <div className="d-flex align-items-center">
//                   <div className="flex-grow-1">
//                     <p className="text-uppercase fw-medium text-muted mb-0">
//                       Net Amount
//                     </p>
//                     <h4
//                       className={`fs-22 fw-semibold ff-secondary mb-0 ${
//                         overallTotals.netAmount >= 0
//                           ? "text-success"
//                           : "text-danger"
//                       }`}
//                     >
//                       {formatCurrency(overallTotals.netAmount)}
//                     </h4>
//                   </div>
//                   <div className="flex-shrink-0">
//                     <div
//                       className={`avatar-sm rounded-circle ${
//                         overallTotals.netAmount >= 0
//                           ? "bg-success bg-opacity-10"
//                           : "bg-danger bg-opacity-10"
//                       }`}
//                     >
//                       <i
//                         className={`fas ${
//                           overallTotals.netAmount >= 0
//                             ? "fa-chart-line"
//                             : "fa-chart-bar"
//                         } ${
//                           overallTotals.netAmount >= 0
//                             ? "text-success"
//                             : "text-danger"
//                         } fs-18`}
//                       ></i>
//                     </div>
//                   </div>
//                 </div>
//               </CardBody>
//             </Card>
//           </Col>

//           <Col lg={3}>
//             <Card className="card-animate">
//               <CardBody>
//                 <div className="d-flex align-items-center">
//                   <div className="flex-grow-1">
//                     <p className="text-uppercase fw-medium text-muted mb-0">
//                       Total Transactions
//                     </p>
//                     <h4 className="fs-22 fw-semibold ff-secondary mb-0 text-primary">
//                       {filteredTransactions.length}
//                     </h4>
//                   </div>
//                   <div className="flex-shrink-0">
//                     <div className="avatar-sm rounded-circle bg-primary bg-opacity-10">
//                       <i className="fas fa-exchange-alt text-primary fs-18"></i>
//                     </div>
//                   </div>
//                 </div>
//               </CardBody>
//             </Card>
//           </Col>
//         </Row>

//         {/* Filters and Actions */}
//         <Card className="mb-4">
//           <CardBody>
//             <Row className="align-items-center">
//               <Col lg={3}>
//                 <div className="mb-3">
//                   <label className="form-label">Start Date</label>
//                   <input
//                     type="date"
//                     className="form-control"
//                     value={startDate}
//                     onChange={e => setStartDate(e.target.value)}
//                   />
//                 </div>
//               </Col>
//               <Col lg={3}>
//                 <div className="mb-3">
//                   <label className="form-label">End Date</label>
//                   <input
//                     type="date"
//                     className="form-control"
//                     value={endDate}
//                     onChange={e => setEndDate(e.target.value)}
//                   />
//                 </div>
//               </Col>
//               <Col lg={6}>
//                 <div className="d-flex align-items-center justify-content-between gap-2 mt-3">
//                   <Button
//                     color="light"
//                     onClick={() => {
//                       setStartDate("")
//                       setEndDate("")
//                     }}
//                   >
//                     <i className="fas fa-times me-1"></i> Clear
//                   </Button>
//                   <Button color="primary" onClick={handleAddTransaction}>
//                     <i className="fas fa-plus me-1"></i> Add Transaction
//                   </Button>
//                 </div>
//               </Col>
//             </Row>
//           </CardBody>
//         </Card>

//         {/* Month-wise Transactions */}
//         {transactionsByMonth.map((monthData, index) => (
//           <Card key={index} className="mb-4">
//             <CardBody>
//               <div className="d-flex justify-content-between align-items-center mb-3">
//                 <h5 className="card-title mb-0">{monthData.monthName}</h5>
//                 <div className="d-flex gap-3">
//                   <span className="text-success">
//                     <i className="fas fa-arrow-up me-1"></i>
//                     Earning: {formatCurrency(monthData.totalEarning)}
//                   </span>
//                   <span className="text-danger">
//                     <i className="fas fa-arrow-down me-1"></i>
//                     Cost: {formatCurrency(monthData.totalCosting)}
//                   </span>
//                   <span
//                     className={
//                       monthData.netAmount >= 0 ? "text-success" : "text-danger"
//                     }
//                   >
//                     <i
//                       className={`fas ${
//                         monthData.netAmount >= 0
//                           ? "fa-chart-line"
//                           : "fa-chart-bar"
//                       } me-1`}
//                     ></i>
//                     Net: {formatCurrency(monthData.netAmount)}
//                   </span>
//                 </div>
//               </div>

//               <TableContainer
//                 columns={columns}
//                 data={monthData.transactions.reverse()}
//                 isGlobalFilter={false}
//                 isPagination={monthData.transactions.length > 10}
//                 SearchPlaceholder="Search transactions..."
//                 pagination="pagination"
//                 paginationWrapper="dataTables_paginate paging_simple_numbers"
//                 tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
//               />
//             </CardBody>
//           </Card>
//         ))}

//         {/* No Data Message */}
//         {transactionsByMonth.length === 0 && (
//           <Card>
//             <CardBody>
//               <div className="text-center py-5">
//                 <i className="fas fa-exchange-alt display-1 text-muted mb-3"></i>
//                 <h4>No Transactions Found</h4>
//                 <p className="text-muted mb-4">
//                   {startDate || endDate
//                     ? "No transactions match your date filter criteria."
//                     : "No transactions recorded yet."}
//                 </p>
//                 <Button color="primary" onClick={handleAddTransaction}>
//                   <i className="fas fa-plus me-2"></i>Add Your First Transaction
//                 </Button>
//               </div>
//             </CardBody>
//           </Card>
//         )}

//         <DeleteModal
//           show={deleteModal}
//           onDeleteClick={handleDeleteTransaction}
//           loader={deletingTransaction}
//           onCloseClick={() => setDeleteModal(false)}
//         />

//         {selectedTransaction && (
//           <EditTransactionModal
//             isOpen={editModalOpen}
//             toggle={() => setEditModalOpen(false)}
//             transaction={selectedTransaction}
//             onTransactionUpdate={handleTransactionUpdate}
//           />
//         )}

//         <AddTransactionModal
//           isOpen={addModalOpen}
//           toggle={() => setAddModalOpen(false)}
//           onTransactionCreate={handleTransactionCreate}
//         />
//       </div>
//     </div>
//   )
// }

// export default Transactions
